'use client';

/**
 * FocusedList — tabbed-list wrapper around DataGrid (was DataTable).
 *
 * Phase F4 S8 upgrade: internals swapped to DataGrid so consumers can opt
 * into v0.2 features (column visibility/reorder/density, page-size
 * selector, isCustomised + reset) via the new optional pass-through
 * props. Existing consumers (assessments, wounds, medications, care-plans,
 * compliance/quality, incidents) keep working without any required prop
 * change — the new v0.2 props are all optional.
 */

import React, { useState, useMemo } from 'react';
import { cn } from '../lib/utils';
import { CountBadge } from './count-badge';
import { DataGrid, type DataGridColumn, type DataGridDensity } from './data-grid';
import type { DataGridBulkAction } from './data-grid/types';
import type { DataTableColumn, BulkAction } from './data-table';

export interface FocusedListTab<T> {
  label: string;
  /** Filter function applied to the full dataset */
  filter?: (item: T) => boolean;
  /** If true, this tab is active by default */
  default?: boolean;
  /** Badge count shown next to the label */
  count?: number;
}

export interface FocusedListProps<T extends Record<string, unknown>> {
  tabs: FocusedListTab<T>[];
  activeTab?: string;
  onTabChange?: (label: string) => void;
  clientPage?: number;
  onClientPageChange?: (page: number) => void;
  sort?: { key: string | null; direction: 'asc' | 'desc' | null };
  onSortChange?: (sort: { key: string | null; direction: 'asc' | 'desc' | null }) => void;
  data: T[];
  columns: DataTableColumn<T>[];
  pageSize?: number;
  emptyMessage?: string;
  onRowClick?: (row: T, index: number) => void;
  className?: string;
  toolbar?: React.ReactNode;
  /** Enable row selection checkboxes */
  selectable?: boolean;
  /** Controlled set of selected row keys */
  selectedRows?: Set<string>;
  /** Called when selection changes */
  onSelectionChange?: (selected: Set<string>) => void;
  /** Function to extract a unique key from a row. Defaults to (row as any).id */
  rowKey?: (row: T) => string;
  /** Bulk actions shown when rows are selected */
  bulkActions?: BulkAction[];
  /**
   * Extra predicate applied AFTER the tab filter. Lets a parent dashboard
   * drive drill-through (e.g. a stat pill sets status=SUBMITTED, which
   * layers on top of the "Action Required" tab). Tab counts reflect the
   * combined (tab ∩ externalFilter) set so users see the real matching count.
   */
  externalFilter?: (item: T) => boolean;

  // ── Phase F4 S8 — DataGrid v0.2 pass-throughs (all optional) ──
  pageSizeOptions?: ReadonlyArray<number>;
  onPageSizeChange?: (size: number) => void;
  columnOrder?: readonly string[];
  onColumnOrderChange?: (nextOrder: readonly string[]) => void;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (key: string, visible: boolean) => void;
  density?: DataGridDensity;
  onDensityChange?: (density: DataGridDensity) => void;
  isCustomised?: boolean;
  onResetCustomisation?: () => void;
}

function FocusedList<T extends Record<string, unknown>>({
  tabs,
  activeTab: controlledTab,
  onTabChange,
  clientPage,
  onClientPageChange,
  sort,
  onSortChange,
  data,
  columns,
  pageSize = 10,
  emptyMessage,
  onRowClick,
  className,
  toolbar,
  selectable,
  selectedRows,
  onSelectionChange,
  rowKey,
  bulkActions,
  externalFilter,
  pageSizeOptions,
  onPageSizeChange,
  columnOrder,
  onColumnOrderChange,
  columnVisibility,
  onColumnVisibilityChange,
  density,
  onDensityChange,
  isCustomised,
  onResetCustomisation,
}: FocusedListProps<T>) {
  const defaultTab = tabs.find((t) => t.default)?.label ?? tabs[0]?.label ?? '';
  const [internalTab, setInternalTab] = useState(defaultTab);
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = onTabChange ?? setInternalTab;

  const activeTabObj = tabs.find((t) => t.label === activeTab) ?? tabs[0];
  const filteredData = useMemo(() => {
    let result = data;
    if (activeTabObj?.filter) result = result.filter(activeTabObj.filter);
    if (externalFilter) result = result.filter(externalFilter);
    return result;
  }, [data, activeTabObj, externalFilter]);

  // DataGrid uses DataGridColumn — DataTableColumn is structurally
  // compatible (same key/header/sortable/render contract).
  const gridColumns = columns as unknown as DataGridColumn<T>[];

  // DataGrid uses a separate `selection` config + flat selectedRows.
  const selection = selectable
    ? {
        rowKey: rowKey ?? ((row: T) => (row as { id?: string }).id ?? ''),
        bulkActions: (bulkActions ?? []) as unknown as ReadonlyArray<DataGridBulkAction>,
      }
    : undefined;

  return (
    <div
      className={cn(
        'min-w-0 overflow-hidden rounded-card border border-border bg-surface-sheet',
        className,
      )}
    >
      {/* Tab bar */}
      <div className="border-b border-slate-200 bg-surface-soft/70">
        <div className="flex max-w-full items-center overflow-x-auto">
          <div className="flex w-max min-w-full items-center gap-0">
            {tabs.map((tab) => {
              const isActive = tab.label === activeTab;
              const tabData = tab.filter ? data.filter(tab.filter) : data;
              const count =
                tab.count ??
                (externalFilter ? tabData.filter(externalFilter).length : tabData.length);
              return (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setActiveTab(tab.label)}
                  className={cn(
                    'relative shrink-0 px-5 py-3 font-body text-body-sm font-medium transition-interactive duration-200',
                    isActive
                      ? '-mb-px border-b-2 border-statura-green-accessible bg-white text-text-heading'
                      : 'text-text-muted hover:bg-white/60 hover:text-text-heading',
                  )}
                >
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    {tab.label}
                    <CountBadge
                      count={count}
                      label={`${tab.label} items`}
                      className={cn(
                        isActive
                          ? 'bg-statura-green-accessible text-white'
                          : 'bg-status-neutral-bg text-status-neutral-fg',
                      )}
                    />
                  </span>
                </button>
              );
            })}
            {toolbar && <div className="ml-auto flex shrink-0 items-center pr-4">{toolbar}</div>}
          </div>
        </div>
      </div>

      {/* Grid */}
      <DataGrid<T>
        className="rounded-none border-0 shadow-none"
        clientPage={clientPage}
        onClientPageChange={onClientPageChange}
        sort={sort}
        onSortChange={onSortChange}
        sortMode="client"
        columns={gridColumns}
        data={filteredData}
        pageSize={pageSize}
        emptyState={{
          title: emptyMessage ?? `No items in "${activeTab}"`,
          description: '',
        }}
        onRowClick={onRowClick}
        selection={selection}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={onPageSizeChange}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        density={density}
        onDensityChange={onDensityChange}
        isCustomised={isCustomised}
        onResetCustomisation={onResetCustomisation}
      />
    </div>
  );
}

export { FocusedList };
